import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button, Modal } from '../components';
import LottieSpinner from '../components/LottieSpinner';
import { getDashboardData, deleteListing as deleteListingApi } from '../api';
import type { DashboardListing, DashboardMatch } from '../types';
import { MATCHES_PAGE_SIZE } from '../constants';
import {
  db, doc, getDoc, collection, Timestamp, addDoc, orderBy,
  query as fsQuery, onSnapshot, updateDoc,
} from '../firebase';

type TabKey = 'all' | 'chat' | 'awaiting';

// --- kleine helpers ------------------------------------------------------

const AVATAR_COLORS = ['#C4536B', '#6B7FA8', '#3F8F72', '#A5713F', '#8A6BA8', '#4E8AA0', '#B0603F'];

const colorFor = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const initial = (name: string) => (name.trim()[0] || '?').toUpperCase();

/** "3 dagen geleden" in de actieve taal, of een datum als het lang geleden is. */
const useRelativeTime = () => {
  const { i18n } = useTranslation();
  return useCallback((date: Date | null) => {
    if (!date) return '';
    const locale = i18n.language || 'nl';
    const diffMs = Date.now() - date.getTime();
    const days = Math.round(diffMs / 86_400_000);
    if (Math.abs(days) >= 30) {
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
    }
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const minutes = Math.round(diffMs / 60_000);
    if (Math.abs(minutes) < 60) return rtf.format(-minutes, 'minute');
    const hours = Math.round(diffMs / 3_600_000);
    if (Math.abs(hours) < 24) return rtf.format(-hours, 'hour');
    return rtf.format(-days, 'day');
  }, [i18n.language]);
};

const Avatar: React.FC<{ name: string; src: string | null; size?: number; onClick?: () => void }> = ({ name, src, size = 40, onClick }) => {
  const [failed, setFailed] = useState(false);
  const style = { width: size, height: size };
  const interactive = onClick
    ? 'cursor-pointer transition-transform duration-150 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-homeme/50'
    : '';

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        style={style}
        onError={() => setFailed(true)}
        onClick={onClick}
        className={`flex-none rounded-full border border-neutral-200 object-cover ${interactive}`}
      />
    );
  }
  return (
    <span
      style={{ ...style, backgroundColor: colorFor(name), fontSize: size * 0.36 }}
      onClick={onClick}
      className={`flex flex-none items-center justify-center rounded-full font-bold text-white ${interactive}`}
    >
      {initial(name)}
    </span>
  );
};

const StatCard: React.FC<{
  label: string;
  value: number;
  accent: string;
  icon: React.ReactNode;
  badge?: { text: string; tone: 'good' | 'warn' };
  foot: string;
}> = ({ label, value, accent, icon, badge, foot }) => (
  <div className="relative overflow-hidden rounded-xl border border-neutral-200 bg-white px-5 py-4">
    <span className="absolute inset-y-0 left-0 w-[3px]" style={{ backgroundColor: accent }} aria-hidden="true" />
    <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-neutral-500">
      <span style={{ color: accent }}>{icon}</span>
      {label}
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-3xl font-extrabold tabular-nums tracking-tight text-neutral-900">{value}</span>
      {badge && (
        <span className={`rounded-md px-1.5 py-0.5 text-xs font-bold ${badge.tone === 'good' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
          {badge.text}
        </span>
      )}
    </div>
    <p className="mt-1.5 text-xs text-neutral-400">{foot}</p>
  </div>
);

// --- pagina --------------------------------------------------------------

export const DashboardPage: React.FC = () => {
  const { currentUser, agentData, setGlobalNotification } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const relative = useRelativeTime();

  // Vertaalfunctie via een ref, zodat effects niet opnieuw lopen bij een taalwissel.
  const tRef = useRef(t);
  tRef.current = t;

  const [data, setData] = useState<DashboardListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('all');
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(MATCHES_PAGE_SIZE);

  const [openMatch, setOpenMatch] = useState<DashboardMatch | null>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const [profileUser, setProfileUser] = useState<any | null>(null);

  // --- laden -------------------------------------------------------------
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        const result = await getDashboardData(currentUser.uid);
        if (cancelled) return;
        setData(result);
        setSelectedId(prev => prev && result.some(r => r.listing.uid === prev) ? prev : result[0]?.listing.uid ?? null);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setFailed(true);
        setGlobalNotification({ message: tRef.current('error_fetching_dashboard'), type: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // `t` bewust niet in de deps: een taalwissel mag geen herlaad veroorzaken.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, setGlobalNotification]);

  // Zet de chat-listener stil als de pagina verdwijnt.
  useEffect(() => () => { unsubscribeRef.current?.(); }, []);

  useEffect(() => { setVisible(MATCHES_PAGE_SIZE); }, [selectedId, tab, search]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, openMatch]);

  // --- afgeleide waarden -------------------------------------------------
  const stats = useMemo(() => {
    const all = data.flatMap(d => d.matches);
    const weekAgo = Date.now() - 7 * 86_400_000;
    const waiting = all.filter(m => m.awaitingReply);
    const oldestWait = waiting.reduce<Date | null>((oldest, m) => {
      const at = m.lastMessageAt;
      if (!at) return oldest;
      return !oldest || at < oldest ? at : oldest;
    }, null);

    return {
      matches: all.length,
      conversations: all.filter(m => m.hasChat).length,
      awaitingReply: waiting.length,
      newThisWeek: all.filter(m => m.createdAt && m.createdAt.getTime() >= weekAgo).length,
      oldestWait,
    };
  }, [data]);

  const selected = useMemo(
    () => data.find(d => d.listing.uid === selectedId) || null,
    [data, selectedId],
  );

  const counts = useMemo(() => {
    const matches = selected?.matches || [];
    return {
      all: matches.length,
      chat: matches.filter(m => m.hasChat).length,
      awaiting: matches.filter(m => m.awaitingReply).length,
    };
  }, [selected]);

  const filtered = useMemo(() => {
    const matches = selected?.matches || [];
    const byTab = tab === 'chat' ? matches.filter(m => m.hasChat)
      : tab === 'awaiting' ? matches.filter(m => m.awaitingReply)
        : matches;
    const needle = search.trim().toLowerCase();
    return needle ? byTab.filter(m => m.name.toLowerCase().includes(needle)) : byTab;
  }, [selected, tab, search]);

  // --- acties ------------------------------------------------------------
  const handleDelete = async (listingId: string, imageUrls: string[]) => {
    if (!window.confirm(t('delete_listing_confirm', 'Weet je zeker dat je deze woning wilt verwijderen? Dit kan niet ongedaan worden gemaakt.'))) return;
    setDeletingId(listingId);
    try {
      await deleteListingApi(listingId, imageUrls);
      const remaining = data.filter(d => d.listing.uid !== listingId);
      setData(remaining);
      if (selectedId === listingId) setSelectedId(remaining[0]?.listing.uid ?? null);
      setGlobalNotification({ message: t('success_listing_deleted'), type: 'success' });
    } catch (err: any) {
      console.error('Error deleting listing:', err);
      setGlobalNotification({ message: err.message || t('error_listing_delete'), type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const openChat = (match: DashboardMatch) => {
    unsubscribeRef.current?.();
    setOpenMatch(match);
    setChatMessages([]);
    setChatLoading(true);
    const q = fsQuery(collection(db, 'chats', match.id, 'messages'), orderBy('timestamp', 'asc'));
    unsubscribeRef.current = onSnapshot(
      q,
      snap => {
        setChatMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setChatLoading(false);
      },
      err => {
        console.error('Chat listener error:', err);
        setChatLoading(false);
        setGlobalNotification({ message: t('error_loading_chat', 'Kan de berichten niet laden.'), type: 'error' });
      },
    );
  };

  const closeChat = () => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    setOpenMatch(null);
    setChatMessages([]);
    setChatInput('');
  };

  const sendMessage = async () => {
    const text = chatInput.trim();
    if (!openMatch || !text || !currentUser) return;
    setSending(true);
    try {
      const now = Timestamp.now();
      await addDoc(collection(db, 'chats', openMatch.id, 'messages'), {
        message: text,
        senderId: currentUser.uid,
        timestamp: now,
        type: 'text',
      });
      // Push naar de zoeker gebeurt server-side (Cloud Function onChatMessageCreated).
      await updateDoc(doc(db, 'matches', openMatch.id), {
        lastMessage: text,
        lastMessageTimestamp: now,
        lastMessageSenderId: currentUser.uid,
      });
      setChatInput('');
      // Houd de lijst gelijk aan wat er zojuist is verstuurd, zonder herladen.
      setData(prev => prev.map(entry => ({
        ...entry,
        matches: entry.matches.map(m => m.id === openMatch.id
          ? { ...m, lastMessage: text, lastMessageAt: now.toDate(), hasChat: true, awaitingReply: false }
          : m),
      })));
      setOpenMatch(m => (m ? { ...m, hasChat: true, awaitingReply: false } : m));
    } catch (err) {
      console.error('Error sending message:', err);
      setGlobalNotification({ message: t('error_sending_message', 'Bericht versturen is niet gelukt.'), type: 'error' });
    } finally {
      setSending(false);
    }
  };

  const openProfile = async (userId: string) => {
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      if (snap.exists()) setProfileUser(snap.data());
      else setGlobalNotification({ message: t('user_not_found', 'Dit profiel bestaat niet meer.'), type: 'error' });
    } catch {
      setGlobalNotification({ message: t('error_loading_profile', 'Kan het profiel niet ophalen.'), type: 'error' });
    }
  };

  // --- rendering ---------------------------------------------------------
  if (loading) {
    return <div className="flex h-[calc(100vh-4rem)] items-center justify-center bg-neutral-100"><LottieSpinner size={100} /></div>;
  }
  if (failed) {
    return (
      <div className="flex min-h-screen items-start justify-center bg-neutral-100 px-4 pt-24">
        <div className="max-w-md rounded-xl border border-neutral-200 bg-white p-8 text-center">
          <p className="text-neutral-700">{t('error_fetching_dashboard')}</p>
          <Button className="mt-4" onClick={() => window.location.reload()}>{t('retry', 'Opnieuw proberen')}</Button>
        </div>
      </div>
    );
  }

  const conversionPercent = stats.matches > 0 ? Math.round((stats.conversations / stats.matches) * 100) : 0;

  return (
    <div className="min-h-screen bg-neutral-100 px-4 pb-12 pt-8 md:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">

        {/* Begroeting */}
        <header className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-neutral-900 md:text-[26px]">
              {t('dashboard_welcome_back', 'Welkom terug, {{name}}', { name: agentData?.contactPerson || t('user', 'gebruiker') })}
            </h1>
            <p className="mt-0.5 text-sm text-neutral-500">
              {agentData?.companyName ? `${agentData.companyName} · ` : ''}
              {t('listing_count', '{{count}} woning', { count: data.length })}
            </p>
          </div>
          <button
            onClick={() => navigate('/add-listing')}
            className="flex items-center gap-2 rounded-lg bg-homeme px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-homeme-dark focus:outline-none focus:ring-2 focus:ring-homeme/50 focus:ring-offset-2"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {t('new_listing', 'Nieuwe woning')}
          </button>
        </header>

        {/* Cijfers */}
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard
            label={t('matches')}
            value={stats.matches}
            accent="#EA4160"
            foot={t('stat_matches_foot', 'Zoekers die op je aanbod reageerden')}
            badge={stats.newThisWeek > 0 ? { text: t('stat_new_week', '+{{count}} deze week', { count: stats.newThisWeek }), tone: 'good' } : undefined}
            icon={<svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 5.6a5.5 5.5 0 0 0-7.8 0L12 6.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l8.8 8.8 8.8-8.8a5.5 5.5 0 0 0 0-7.8z" /></svg>}
          />
          <StatCard
            label={t('stat_conversations', 'Gesprekken')}
            value={stats.conversations}
            accent="#0E9F6E"
            foot={stats.matches > 0
              ? t('stat_conversations_foot', '{{percent}}% van je matches stuurde een bericht', { percent: conversionPercent })
              : t('stat_conversations_empty', 'Nog geen berichten ontvangen')}
            icon={<svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.4 8.4 0 0 1-9-8.4A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" /><path d="M8 20.5 4.5 22l1-3.8" /></svg>}
          />
          <StatCard
            label={t('stat_awaiting', 'Wacht op je reactie')}
            value={stats.awaitingReply}
            accent={stats.awaitingReply > 0 ? '#B4761A' : '#A3A3A3'}
            badge={stats.awaitingReply > 0 ? { text: t('stat_action', 'actie'), tone: 'warn' } : undefined}
            foot={stats.awaitingReply > 0 && stats.oldestWait
              ? t('stat_awaiting_foot', 'Langst wachtend: {{time}}', { time: relative(stats.oldestWait) })
              : t('stat_awaiting_empty', 'Alles beantwoord')}
            icon={<svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" /></svg>}
          />
        </div>

        {data.length === 0 ? (
          <div className="rounded-xl border border-neutral-200 bg-white py-20 text-center">
            <svg className="mx-auto mb-4 h-16 w-16 text-neutral-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 10.5 12 3l9 7.5" /><path d="M5.5 9.5V20h13V9.5" />
            </svg>
            <p className="text-lg font-semibold text-neutral-700">{t('no_listings')}</p>
            <p className="mt-1 text-neutral-500">{t('add_first_listing')}</p>
            <Button className="mt-5" onClick={() => navigate('/add-listing')}>{t('new_listing', 'Nieuwe woning')}</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">

            {/* Woningen */}
            <aside className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
              <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
                <span className="text-xs font-bold uppercase tracking-wide text-neutral-500">{t('my_listings')}</span>
                <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[11px] font-bold tabular-nums text-neutral-600">{data.length}</span>
              </div>
              <ul className="max-h-[520px] overflow-y-auto">
                {data.map(({ listing, matches }) => {
                  const isSelected = listing.uid === selectedId;
                  const image = listing.profileImages?.[0] || listing.listing?.roomImages?.[0] || null;
                  const waiting = matches.filter(m => m.awaitingReply).length;
                  return (
                    <li key={listing.uid}>
                      <button
                        onClick={() => setSelectedId(listing.uid || null)}
                        aria-current={isSelected}
                        className={`flex w-full items-center gap-3 border-b border-neutral-100 px-3.5 py-3 text-left transition-colors last:border-b-0
                          ${isSelected ? 'bg-homeme/[0.07] shadow-[inset_3px_0_0_#EA4160]' : 'hover:bg-neutral-50'}`}
                      >
                        {image ? (
                          <img src={image} alt="" className="flex-none rounded-lg border border-neutral-200 object-cover" style={{ width: 52, height: 52 }} />
                        ) : (
                          <span className="flex flex-none items-center justify-center rounded-lg bg-neutral-200 text-neutral-400" style={{ width: 52, height: 52 }}>
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 10 12 3l9 7" /><path d="M5.5 9v11h13V9" /></svg>
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-bold text-neutral-900">
                            {listing.listing?.location?.address || t('no_address', 'Geen adres')}
                          </span>
                          <span className="block truncate text-xs tabular-nums text-neutral-500">
                            €{listing.listing?.price ?? 0} · {listing.listing?.placeType} · {listing.listing?.squareMeters ?? 0} m²
                          </span>
                          <span className="mt-1.5 flex flex-wrap gap-1.5">
                            <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[11px] font-bold tabular-nums text-neutral-600">
                              {t('match_count', '{{count}} matches', { count: matches.length })}
                            </span>
                            {waiting > 0 && (
                              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold tabular-nums text-amber-700">
                                {t('waiting_count', '{{count}} wacht', { count: waiting })}
                              </span>
                            )}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Reacties op de gekozen woning */}
            <section className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
              {selected && (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3">
                    <div className="min-w-0">
                      <h2 className="truncate text-base font-bold text-neutral-900">
                        {t('responses', 'Reacties')} · {selected.listing.listing?.location?.address || t('no_address', 'Geen adres')}
                      </h2>
                    </div>
                    <div className="flex flex-none gap-2">
                      <Button size="small" variant="secondary" onClick={() => navigate(`/edit-listing/${selected.listing.uid}`)}>{t('edit')}</Button>
                      <Button
                        size="small"
                        variant="danger"
                        isLoading={deletingId === selected.listing.uid}
                        disabled={deletingId === selected.listing.uid}
                        onClick={() => handleDelete(
                          selected.listing.uid!,
                          [...(selected.listing.profileImages || []), ...(selected.listing.listing?.roomImages || [])],
                        )}
                      >
                        {t('delete')}
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-end justify-between gap-2 border-b border-neutral-200 px-3 pt-2.5">
                    <div className="flex gap-1">
                      {([
                        ['all', t('tab_all', 'Alle'), counts.all],
                        ['chat', t('tab_with_message', 'Met bericht'), counts.chat],
                        ['awaiting', t('tab_awaiting', 'Wacht op jou'), counts.awaiting],
                      ] as [TabKey, string, number][]).map(([key, labelText, count]) => (
                        <button
                          key={key}
                          onClick={() => setTab(key)}
                          className={`-mb-px flex items-center gap-1.5 rounded-t-lg border border-b-0 px-3 py-2 text-sm transition-colors
                            ${tab === key
                              ? 'border-neutral-200 bg-white font-bold text-neutral-900'
                              : 'border-transparent font-medium text-neutral-500 hover:text-neutral-800'}`}
                        >
                          {labelText}
                          <span className={`rounded-full px-1.5 text-[11px] font-bold tabular-nums ${tab === key ? 'bg-homeme text-white' : 'border border-neutral-200 bg-neutral-50 text-neutral-600'}`}>
                            {count}
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="mb-2 flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 focus-within:border-homeme focus-within:ring-2 focus-within:ring-homeme/20">
                      <svg className="h-3.5 w-3.5 flex-none text-neutral-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
                      <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('search_by_name', 'Zoek op naam')}
                        aria-label={t('search_by_name', 'Zoek op naam')}
                        className="w-28 bg-transparent text-xs text-neutral-700 placeholder-neutral-400 focus:outline-none sm:w-36"
                      />
                    </div>
                  </div>

                  {filtered.length === 0 ? (
                    <p className="px-4 py-16 text-center text-sm text-neutral-500">
                      {search.trim()
                        ? t('no_search_results', 'Geen reacties gevonden voor “{{query}}”.', { query: search.trim() })
                        : tab === 'awaiting' ? t('nothing_waiting', 'Je hebt alles beantwoord.')
                          : tab === 'chat' ? t('no_conversations_yet', 'Nog geen berichten op deze woning.')
                            : t('no_matches_for_listing')}
                    </p>
                  ) : (
                    <>
                      <ul className="max-h-[420px] overflow-y-auto">
                        {filtered.slice(0, visible).map(match => (
                          <li key={match.id} className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-start gap-3 border-b border-neutral-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-neutral-50">
                            <Avatar name={match.name} src={match.profileImage} onClick={() => openProfile(match.otherUserId)} />
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  onClick={() => openProfile(match.otherUserId)}
                                  className="text-sm font-bold text-neutral-900 hover:text-homeme focus:outline-none focus:underline"
                                >
                                  {match.name}
                                </button>
                                {match.awaitingReply && <span className="h-1.5 w-1.5 flex-none rounded-full bg-homeme" title={t('awaits_you', 'Wacht op jou')} />}
                                <span className="text-xs tabular-nums text-neutral-400">
                                  {match.hasChat ? relative(match.lastMessageAt) : t('no_message_yet', 'Nog geen bericht')}
                                </span>
                              </div>
                              <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-neutral-500">
                                {match.hasChat
                                  ? match.lastMessage
                                  : <em className="not-italic text-neutral-400">
                                      {t('match_since', 'Match sinds {{date}} · stuur de eerste vraag', {
                                        date: match.createdAt ? match.createdAt.toLocaleDateString(i18n.language || 'nl', { day: 'numeric', month: 'long' }) : '—',
                                      })}
                                    </em>}
                              </p>
                            </div>
                            <Button size="small" variant={match.hasChat ? 'primary' : 'outline'} onClick={() => openChat(match)} className="whitespace-nowrap">
                              {match.hasChat ? t('view_chat') : t('start_chat')}
                            </Button>
                          </li>
                        ))}
                      </ul>
                      <div className="border-t border-neutral-100 px-4 py-2.5 text-center text-xs tabular-nums text-neutral-400">
                        {visible < filtered.length ? (
                          <button onClick={() => setVisible(v => v + MATCHES_PAGE_SIZE)} className="font-bold text-homeme hover:underline focus:outline-none focus:underline">
                            {t('show_more_of', 'Toon meer ({{shown}} van {{total}})', { shown: visible, total: filtered.length })}
                          </button>
                        ) : (
                          t('showing_all', 'Alle {{total}} getoond', { total: filtered.length })
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </section>
          </div>
        )}
      </div>

      {/* Chat */}
      <Modal
        isOpen={!!openMatch}
        onClose={closeChat}
        size="lg"
        title={openMatch ? t('chat_with', { name: openMatch.name }) : 'Chat'}
        subtitle={selected?.listing.listing?.location?.address}
      >
        <div className="flex h-[60vh] flex-col">
          <div className="flex-1 space-y-2 overflow-y-auto bg-neutral-50 px-5 py-4">
            {chatLoading ? (
              <div className="flex h-full items-center justify-center"><LottieSpinner size={64} /></div>
            ) : chatMessages.length === 0 ? (
              <p className="pt-12 text-center text-sm text-neutral-400">{t('no_messages_yet', 'Nog geen berichten. Stuur de eerste.')}</p>
            ) : (
              <>
                {chatMessages.map(msg => {
                  const mine = msg.senderId === currentUser?.uid;
                  const at = msg.timestamp?.toDate ? msg.timestamp.toDate() : null;
                  return (
                    <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[78%] whitespace-pre-line break-words rounded-2xl px-4 py-2.5 text-sm ${mine ? 'rounded-br-md bg-homeme text-white' : 'rounded-bl-md border border-neutral-200 bg-white text-neutral-800'}`}>
                        {msg.message}
                        {at && (
                          <div className={`mt-1 text-right text-[11px] tabular-nums ${mine ? 'text-white/70' : 'text-neutral-400'}`}>
                            {at.toLocaleDateString(i18n.language || 'nl', { day: 'numeric', month: 'short' })}, {at.toLocaleTimeString(i18n.language || 'nl', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </>
            )}
          </div>
          <div className="flex gap-2 border-t border-neutral-100 bg-white px-5 py-3.5">
            <input
              className="flex-1 rounded-lg border border-neutral-300 px-3.5 py-2.5 text-sm focus:border-homeme focus:outline-none focus:ring-2 focus:ring-homeme/25"
              type="text"
              placeholder={t('type_message', 'Typ een bericht...')}
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              disabled={sending}
              autoFocus
            />
            <Button onClick={sendMessage} isLoading={sending} disabled={sending || !chatInput.trim()}>{t('send', 'Verstuur')}</Button>
          </div>
        </div>
      </Modal>

      {/* Profiel van de zoeker */}
      <Modal isOpen={!!profileUser} onClose={() => setProfileUser(null)} size="lg" title={profileUser?.firstName || t('profile', 'Profiel')}>
        {profileUser && (
          <div className="px-6 py-5">
            <div className="mb-5 flex items-center gap-4">
              <Avatar name={profileUser.firstName || '?'} src={profileUser.profileImages?.[0] || null} size={72} />
              <div>
                <h3 className="text-lg font-bold text-neutral-900">{profileUser.firstName}</h3>
                {profileUser.age && <p className="text-sm text-neutral-500">{t('age_years', '{{count}} jaar', { count: profileUser.age })}</p>}
              </div>
            </div>

            {profileUser.description && (
              <div className="mb-4 rounded-lg bg-neutral-50 p-4">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-neutral-500">{t('description', 'Beschrijving')}</span>
                <ExpandableText text={profileUser.description} />
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {profileUser.preferences?.budget && (
                <InfoTile label={t('budget', 'Budget')}>€{profileUser.preferences.budget.min} – €{profileUser.preferences.budget.max}</InfoTile>
              )}
              {profileUser.preferences?.placeType && (
                <InfoTile label={t('place_type', 'Woningtype')}>
                  {Array.isArray(profileUser.preferences.placeType) ? profileUser.preferences.placeType.join(', ') : profileUser.preferences.placeType}
                </InfoTile>
              )}
              {profileUser.preferences?.location?.address && (
                <InfoTile label={t('location', 'Locatie')}>
                  {profileUser.preferences.location.address}
                  {profileUser.preferences.location.searchRadius && (
                    <span className="mt-1 block text-xs text-neutral-500">{t('search_radius', 'Zoekradius')}: {profileUser.preferences.location.searchRadius} km</span>
                  )}
                </InfoTile>
              )}
              {profileUser.preferences?.interests?.length > 0 && (
                <InfoTile label={t('interests', 'Interesses')}>{profileUser.preferences.interests.join(', ')}</InfoTile>
              )}
              {profileUser.preferences?.roommatePreferences && (
                <InfoTile label={t('roommate_preferences', 'Huisgenoot voorkeur')} wide>
                  {profileUser.preferences.roommatePreferences.occupation?.length > 0 && (
                    <div><span className="font-medium">{t('occupation', 'Beroep')}:</span> {profileUser.preferences.roommatePreferences.occupation.join(', ')}</div>
                  )}
                  {profileUser.preferences.roommatePreferences.gender && (
                    <div><span className="font-medium">{t('gender', 'Geslacht')}:</span> {profileUser.preferences.roommatePreferences.gender}</div>
                  )}
                  {profileUser.preferences.roommatePreferences.ageRange && (
                    <div><span className="font-medium">{t('age', 'Leeftijd')}:</span> {profileUser.preferences.roommatePreferences.ageRange.min} – {profileUser.preferences.roommatePreferences.ageRange.max}</div>
                  )}
                </InfoTile>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const InfoTile: React.FC<{ label: string; wide?: boolean; children: React.ReactNode }> = ({ label, wide, children }) => (
  <div className={`rounded-lg bg-neutral-50 p-3.5 text-sm text-neutral-700 ${wide ? 'md:col-span-2' : ''}`}>
    <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-neutral-500">{label}</span>
    {children}
  </div>
);

const ExpandableText: React.FC<{ text: string }> = ({ text }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const lines = text.split('\n');
  const isLong = lines.length > 6 || text.length > 420;
  const shown = expanded || !isLong ? text : `${lines.slice(0, 6).join('\n').slice(0, 420)}…`;

  return (
    <div className="text-sm text-neutral-700">
      <span className="whitespace-pre-line">{shown}</span>
      {isLong && (
        <button
          className="ml-1 text-xs font-bold text-homeme hover:underline focus:outline-none focus:underline"
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? t('show_less', 'Minder weergeven') : t('show_more', 'Meer weergeven')}
        </button>
      )}
    </div>
  );
};
